import React, { useState } from 'react';
import { Shield, CheckCircle, Users, Building2, TrendingUp, FileCheck, Calculator, Phone, Mail, MapPin, ChevronRight, Send, User, MessageSquare } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

// Determine API URL based on environment
// Detects if we're in production/preview and uses appropriate URL
const getApiUrl = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const currentHost = typeof window !== 'undefined' ? window.location.hostname : '';
  
  // If we're NOT on localhost (deployed environment)
  if (currentHost && !currentHost.includes('localhost') && !currentHost.includes('127.0.0.1')) {
    // Use relative URL - nginx will route /api to backend
    return '/api';
  }
  
  // In local development, use explicit backend URL
  if (backendUrl) {
    return `${backendUrl}/api`;
  }
  
  // Fallback to relative URL
  return '/api';
};

const API = getApiUrl();

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    reason: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const apiUrl = `${API}/contact`;
      console.log('=== Contact Form Debug Info ===');
      console.log('Current hostname:', window.location.hostname);
      console.log('REACT_APP_BACKEND_URL:', process.env.REACT_APP_BACKEND_URL);
      console.log('API URL being used:', apiUrl);
      console.log('Form data:', formData);
      console.log('=============================');
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);

      if (response.ok) {
        setIsSuccess(true);
        setFormData({ name: '', email: '', reason: '' });
      } else {
        setError(data.detail || 'Failed to send message. Please try again.');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      console.error('Error details:', {
        message: err.message,
        name: err.name,
        stack: err.stack
      });
      setError(`Network error: ${err.message}. Please try again or email us directly at info@honestaccountants.ca`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
  {
    icon: Calculator,
    title: 'Accounting',
    items: [
    'Implementation of reporting systems',
    'Financial statement preparation, analysis, and interpretation',
    'Analysis of financial reports']

  },
  {
    icon: FileCheck,
    title: 'Bookkeeping',
    items: [
    'Regular bookkeeping services',
    'Remittances for government taxes, HST, WSIB, and union dues']

  },
  {
    icon: TrendingUp,
    title: 'Taxation',
    items: [
    'Corporation income tax preparation and planning',
    'Compensation planning for small business owners',
    'Tax loss utilization and strategizing',
    'Holdings companies and corporate structures']

  }];


  const industries = [
  'Salons', 'Restaurants', 'Air BnB', 'FBA Companies', 'Auto Repair',
  'IT Services', 'Construction', 'Healthcare', 'Real Estate',
  'Food & Beverage', 'HVAC', 'Transportation'];


  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header/Navigation */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center">
              <img
                src="/logo.png"
                alt="Honest Accountants Logo"
                className="h-24 w-auto transition-transform duration-300 hover:scale-105" />

            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('about')} className="text-slate-700 hover:text-[#0F2A44] transition-colors duration-200 font-medium">About</button>
              <button onClick={() => scrollToSection('services')} className="text-slate-700 hover:text-[#0F2A44] transition-colors duration-200 font-medium">Services</button>
              <button onClick={() => scrollToSection('industries')} className="text-slate-700 hover:text-[#0F2A44] transition-colors duration-200 font-medium">Industries</button>
              <button onClick={() => scrollToSection('contact')} className="bg-[#0F2A44] text-white px-6 py-2.5 rounded-lg hover:bg-[#1a3a5c] transition-all duration-200 font-medium shadow-md hover:shadow-lg">Contact Us</button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section - Enhanced */}
      <section className="relative py-24 lg:py-36 overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-72 h-72 bg-teal-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
          <div className="absolute top-40 left-20 w-72 h-72 bg-[#0F2A44] rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 right-40 w-72 h-72 bg-slate-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8 animate-fade-in-up">
              <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-teal-50 to-teal-100 text-teal-700 px-5 py-2.5 rounded-full shadow-sm">
                <Shield className="w-4 h-4" />
                <span className="text-sm font-semibold">Trusted Referral Network</span>
              </div>
              <h1 className="text-5xl lg:text-7xl font-bold text-[#0F2A44] leading-tight">
                Accounting referrals you can <span className="bg-gradient-to-r from-teal-600 to-teal-500 bg-clip-text text-transparent">trust</span>.
              </h1>
              <p className="text-xl lg:text-2xl text-slate-600 leading-relaxed">
                Honest Accountants connects Ontario businesses with vetted, independent accounting firms known for integrity, compliance, and sound financial advice.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <button onClick={() => scrollToSection('contact')} className="inline-flex items-center justify-center bg-[#0F2A44] text-white px-10 py-5 rounded-xl hover:bg-[#1a3a5c] transition-all duration-300 font-semibold text-lg shadow-xl hover:shadow-2xl hover:scale-105 group">
                  Request an Introduction
                  <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-2 transition-transform" />
                </button>
                <button onClick={() => scrollToSection('about')} className="inline-flex items-center justify-center border-2 border-[#0F2A44] text-[#0F2A44] px-10 py-5 rounded-xl hover:bg-[#0F2A44] hover:text-white transition-all duration-300 font-semibold text-lg hover:shadow-xl">
                  How It Works
                </button>
              </div>
            </div>
            <div className="relative animate-fade-in-right">
              <div className="absolute -inset-8 bg-gradient-to-r from-teal-200 via-teal-100 to-transparent rounded-3xl opacity-30 blur-3xl"></div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHw0fHxmaW5hbmNpYWwlMjBwbGFubmluZ3xlbnwwfHx8fDE3NjY0MTczODN8MA&ixlib=rb-4.1.0&q=85"
                  alt="Financial Planning"
                  className="relative rounded-3xl shadow-2xl w-full h-auto object-cover border-4 border-white" />

                <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-xl p-6 border-2 border-teal-100">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-teal-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-700">Vetted Partners</p>
                      <p className="text-xs text-slate-500">Ontario-wide network</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Are Section - Enhanced */}
      <section id="about" className="py-24 bg-white relative overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center space-y-6 mb-16">
            <div className="inline-block">
              <div className="bg-gradient-to-r from-teal-500 to-teal-600 text-white text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
                Our Difference
              </div>
            </div>
            <h2 className="text-4xl lg:text-6xl font-bold text-[#0F2A44] mb-6 leading-tight">
              Not a marketplace. Not a call centre. <br />
              <span className="bg-gradient-to-r from-teal-600 to-teal-500 bg-clip-text text-transparent">Not a lead farm.</span>
            </h2>
            <p className="text-xl lg:text-2xl text-slate-600 leading-relaxed mb-8">
              Honest Accountants is a referral network built for business owners who value professionalism, discretion, and long-term financial clarity.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed max-w-3xl mx-auto">
              We work with a small group of licensed accounting firms who meet our standards for ethics, experience, and client care — and we introduce businesses only when there's a genuine fit.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <Card className="border-2 border-slate-100 hover:border-teal-300 hover:shadow-2xl transition-all duration-500 group relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-teal-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <CardHeader className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-2xl text-[#0F2A44] mb-3">Vetted Partners</CardTitle>
              </CardHeader>
              <CardContent className="relative">
                <p className="text-slate-600 leading-relaxed text-lg">
                  Every accounting firm in our network is thoroughly vetted for integrity, compliance, and professional excellence.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-slate-100 hover:border-teal-300 hover:shadow-2xl transition-all duration-500 group relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-teal-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <CardHeader className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-2xl text-[#0F2A44] mb-3">Genuine Fit</CardTitle>
              </CardHeader>
              <CardContent className="relative">
                <p className="text-slate-600 leading-relaxed text-lg">
                  We match businesses with accountants based on industry expertise, business size, and specific needs.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-slate-100 hover:border-teal-300 hover:shadow-2xl transition-all duration-500 group relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-teal-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <CardHeader className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-2xl text-[#0F2A44] mb-3">Independent Firms</CardTitle>
              </CardHeader>
              <CardContent className="relative">
                <p className="text-slate-600 leading-relaxed text-lg">
                  Our network consists of independent professional corporations, not employees or affiliates.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#0F2A44] mb-4">Comprehensive Services</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Our network partners offer a full range of accounting, bookkeeping, and tax services for businesses and individuals.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="border-2 border-slate-100 hover:border-[#0F2A44] hover:shadow-xl transition-all duration-300 group">
                  <CardHeader>
                    <div className="w-14 h-14 bg-[#0F2A44] rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <CardTitle className="text-2xl text-[#0F2A44]">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.items.map((item, idx) =>
                      <li key={idx} className="flex items-start text-slate-600">
                          <ChevronRight className="w-4 h-4 mt-1 mr-2 text-teal-600 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      )}
                    </ul>
                  </CardContent>
                </Card>);

            })}
          </div>

          <div className="max-w-4xl mx-auto">
            <Card className="border-2 border-slate-200">
              <CardHeader>
                <CardTitle className="text-2xl text-[#0F2A44]">Additional Services</CardTitle>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="item-1">
                    <AccordionTrigger className="text-lg font-semibold text-[#0F2A44] hover:text-teal-600">
                      Government Reporting Assistance
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-600">
                      <ul className="space-y-2 mt-2">
                        <li className="flex items-start">
                          <ChevronRight className="w-4 h-4 mt-1 mr-2 text-teal-600 flex-shrink-0" />
                          <span>HST, EHT, and WSIB returns</span>
                        </li>
                        <li className="flex items-start">
                          <ChevronRight className="w-4 h-4 mt-1 mr-2 text-teal-600 flex-shrink-0" />
                          <span>T4 and T5 summaries</span>
                        </li>
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="item-2">
                    <AccordionTrigger className="text-lg font-semibold text-[#0F2A44] hover:text-teal-600">
                      Individual Tax Services
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-600">
                      <ul className="space-y-2 mt-2">
                        <li className="flex items-start">
                          <ChevronRight className="w-4 h-4 mt-1 mr-2 text-teal-600 flex-shrink-0" />
                          <span>Annual income tax return preparation</span>
                        </li>
                        <li className="flex items-start">
                          <ChevronRight className="w-4 h-4 mt-1 mr-2 text-teal-600 flex-shrink-0" />
                          <span>Proactive tax planning for individuals and families</span>
                        </li>
                        <li className="flex items-start">
                          <ChevronRight className="w-4 h-4 mt-1 mr-2 text-teal-600 flex-shrink-0" />
                          <span>Income tax splitting strategies</span>
                        </li>
                        <li className="flex items-start">
                          <ChevronRight className="w-4 h-4 mt-1 mr-2 text-teal-600 flex-shrink-0" />
                          <span>Personal net worth statements</span>
                        </li>
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="item-3">
                    <AccordionTrigger className="text-lg font-semibold text-[#0F2A44] hover:text-teal-600">
                      Voluntary Disclosure
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-600">
                      <p>Assistance with voluntary disclosure to Canada Revenue Agency, potentially eliminating penalties on unreported income for previous years.</p>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section id="industries" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#0F2A44] mb-4">Industries We Serve</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Our network partners have extensive experience across diverse business sectors.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {industries.map((industry, index) =>
            <div
              key={index}
              className="bg-slate-50 border-2 border-slate-100 rounded-lg p-4 text-center hover:border-teal-200 hover:shadow-md transition-all duration-200 group">

                <Building2 className="w-6 h-6 text-teal-600 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                <p className="text-slate-700 font-medium">{industry}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Network Partner Section - Enhanced */}
      <section className="py-24 bg-gradient-to-br from-slate-50 via-white to-teal-50/30 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-teal-100 rounded-full blur-3xl opacity-20"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#0F2A44] rounded-full blur-3xl opacity-5"></div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#0F2A44] mb-4">
              Our Accounting Partners
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Our network consists of independent professional corporations, not employees or affiliates. Each firm operates independently and maintains full professional responsibility for its work.
            </p>
          </div>

          {/* Featured Partner: D'Silva Professional Corp */}
          <div className="max-w-6xl mx-auto">
            <Card className="border-2 border-slate-200 shadow-2xl overflow-hidden hover:shadow-3xl transition-shadow duration-300">
              <div className="grid lg:grid-cols-5 gap-0">
                {/* Image Column */}
                <div className="lg:col-span-2 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#0F2A44]/90 to-teal-600/80 z-10"></div>
                  <img
                    src="https://images.pexels.com/photos/27086185/pexels-photo-27086185.jpeg"
                    alt="Professional Team"
                    className="w-full h-full object-cover min-h-[400px]" />

                  <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-white p-8">
                    <Shield className="w-16 h-16 mb-4 text-teal-200" />
                    <h3 className="text-2xl font-bold text-center mb-2">Featured Partner</h3>
                    <p className="text-center text-sm text-slate-200">Established GTA Accounting Firm</p>
                  </div>
                </div>

                {/* Content Column */}
                <div className="lg:col-span-3 p-8 lg:p-12">
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-3xl font-bold text-[#0F2A44] mb-2">
                        D'Silva Professional Corp.
                      </h3>
                      <a
                        href="https://www.dsilvaprofessionalcorp.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-teal-600 hover:text-teal-700 font-medium inline-flex items-center group transition-colors">

                        Visit Website
                        <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                      </a>
                    </div>

                    <p className="text-lg text-slate-600 leading-relaxed border-l-4 border-teal-500 pl-4 italic">
                      A professional and progressive firm specializing in providing accounting and tax solutions for small and medium-sized businesses.
                    </p>

                    <div className="space-y-3">
                      <p className="text-slate-700 leading-relaxed">
                        An established accounting firm in the GTA, offering a comprehensive range of accounting, tax, and related services. Their dedicated team of knowledgeable professionals focuses on creating customized accounting services tailored to meet the needs of both entrepreneurs and not-for-profit organizations.
                      </p>
                      <p className="text-slate-700 leading-relaxed">
                        They believe in not just providing clients with quality service but are also committed to building, maintaining, and sustaining long-term relationships with all their clients.
                      </p>
                    </div>

                    {/* Core Services */}
                    <div className="grid sm:grid-cols-2 gap-3 pt-4">
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                          <CheckCircle className="w-5 h-5 text-teal-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-[#0F2A44]">Bookkeeping & Payroll</h4>
                          <p className="text-sm text-slate-600">Comprehensive financial management</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                          <CheckCircle className="w-5 h-5 text-teal-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-[#0F2A44]">Tax Services</h4>
                          <p className="text-sm text-slate-600">Corporate & personal income tax</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                          <CheckCircle className="w-5 h-5 text-teal-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-[#0F2A44]">Compliance</h4>
                          <p className="text-sm text-slate-600">Government reporting & filings</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                          <CheckCircle className="w-5 h-5 text-teal-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-[#0F2A44]">Business Setup</h4>
                          <p className="text-sm text-slate-600">Incorporations for individuals</p>
                        </div>
                      </div>
                    </div>

                    {/* Contact Info */}
                    <div className="flex flex-wrap gap-4 pt-4 border-t border-slate-200">
                      <a
                        href="mailto:info@dpccga.com"
                        className="inline-flex items-center text-sm text-slate-600 hover:text-teal-600 transition-colors">

                        <Mail className="w-4 h-4 mr-2" />
                        info@dpccga.com
                      </a>
                      <a
                        href="tel:+16479228118"
                        className="inline-flex items-center text-sm text-slate-600 hover:text-teal-600 transition-colors">

                        <Phone className="w-4 h-4 mr-2" />
                        (647) 922-8118
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Additional Information */}
          <div className="max-w-4xl mx-auto mt-12 text-center">
            <p className="text-slate-600 leading-relaxed">
              <strong className="text-[#0F2A44]">Note:</strong> D'Silva Professional Corp. is an independent professional corporation. They operate independently and maintain full professional responsibility for their work. Honest Accountants facilitates introductions based on client needs and firm expertise.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section - Static at Bottom */}
      <section id="contact" className="relative py-24 bg-gradient-to-br from-[#0F2A44] via-[#1a3a5c] to-[#0F2A44] text-white overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-teal-500 rounded-full mix-blend-overlay filter blur-3xl opacity-20 animate-blob"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-400 rounded-full mix-blend-overlay filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-4xl mx-auto">
            {!isSuccess ? (
              <>
                {/* Header */}
                <div className="text-center mb-12">
                  <div className="inline-block mb-4">
                    <div className="bg-white/10 backdrop-blur-sm text-white text-sm font-semibold px-4 py-1.5 rounded-full border border-white/20">
                      Ready to Get Started?
                    </div>
                  </div>
                  <h2 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
                    Get in Touch
                  </h2>
                  <p className="text-xl lg:text-2xl text-slate-200 leading-relaxed">
                    Fill out the form below and we'll get back to you within 1-2 business days.
                  </p>
                </div>

                {/* Contact Form */}
                <Card className="bg-white/95 backdrop-blur-sm border-2 border-white/20 shadow-2xl">
                  <CardContent className="p-8 lg:p-12">
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Name Field */}
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-[#0F2A44] font-semibold flex items-center text-lg">
                          <User className="w-5 h-5 mr-2" />
                          Name *
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleFormChange}
                          placeholder="Your full name"
                          className="border-2 border-slate-200 focus:border-teal-500 transition-colors h-12 text-base"
                        />
                      </div>

                      {/* Email Field */}
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-[#0F2A44] font-semibold flex items-center text-lg">
                          <Mail className="w-5 h-5 mr-2" />
                          Email *
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleFormChange}
                          placeholder="your@email.com"
                          className="border-2 border-slate-200 focus:border-teal-500 transition-colors h-12 text-base"
                        />
                      </div>

                      {/* Reason Field */}
                      <div className="space-y-2">
                        <Label htmlFor="reason" className="text-[#0F2A44] font-semibold flex items-center text-lg">
                          <MessageSquare className="w-5 h-5 mr-2" />
                          Reason for Inquiry *
                        </Label>
                        <Textarea
                          id="reason"
                          name="reason"
                          required
                          value={formData.reason}
                          onChange={handleFormChange}
                          placeholder="Please describe how we can help you..."
                          rows={6}
                          className="border-2 border-slate-200 focus:border-teal-500 transition-colors resize-none text-base"
                        />
                      </div>

                      {/* Error Message */}
                      {error && (
                        <div className="p-4 bg-red-50 border-2 border-red-200 rounded-lg">
                          <p className="text-red-700 text-sm">{error}</p>
                        </div>
                      )}

                      {/* Submit Button */}
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-gradient-to-r from-[#0F2A44] to-[#1a3a5c] hover:from-[#1a3a5c] hover:to-[#0F2A44] text-white py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Sending...
                          </span>
                        ) : (
                          <span className="flex items-center justify-center">
                            <Send className="w-5 h-5 mr-2" />
                            Send Message
                          </span>
                        )}
                      </Button>

                      <p className="text-xs text-slate-500 text-center">
                        By submitting this form, you agree to be contacted by Honest Accountants.
                      </p>
                    </form>
                  </CardContent>
                </Card>

                {/* Contact Info */}
                <div className="mt-12 grid md:grid-cols-3 gap-8 max-w-3xl mx-auto">
                  <div className="text-center space-y-2">
                    <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-3 border border-white/20">
                      <CheckCircle className="w-6 h-6 text-teal-300" />
                    </div>
                    <h3 className="font-semibold text-lg">Vetted Professionals</h3>
                    <p className="text-sm text-slate-300">Trusted accounting partners</p>
                  </div>
                  <div className="text-center space-y-2">
                    <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-3 border border-white/20">
                      <Shield className="w-6 h-6 text-teal-300" />
                    </div>
                    <h3 className="font-semibold text-lg">Ontario-Wide</h3>
                    <p className="text-sm text-slate-300">Serving businesses across Ontario</p>
                  </div>
                  <div className="text-center space-y-2">
                    <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-3 border border-white/20">
                      <Users className="w-6 h-6 text-teal-300" />
                    </div>
                    <h3 className="font-semibold text-lg">Perfect Match</h3>
                    <p className="text-sm text-slate-300">Tailored to your needs</p>
                  </div>
                </div>
              </>
            ) : (
              /* Success Message */
              <div className="text-center py-16 animate-fade-in">
                <div className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-8 border-4 border-white/30">
                  <CheckCircle className="w-12 h-12 text-white" />
                </div>
                <h3 className="text-4xl lg:text-5xl font-bold mb-6">Message Sent!</h3>
                <p className="text-2xl text-slate-200 mb-4">
                  Thank you for reaching out.
                </p>
                <p className="text-xl text-slate-300 mb-8">
                  We'll get back to you within 1-2 business days.
                </p>
                <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full border border-white/20">
                  <Mail className="w-5 h-5" />
                  <span className="font-medium">info@honestaccountants.ca</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F2A44] border-t border-slate-700 py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <img
                src="/logo.png"
                alt="Honest Accountants Logo"
                className="h-auto w-auto mb-4" />

              <p className="text-sm leading-relaxed !ml-[25px] !mr-[02px] text-slate-300">
                Connecting Ontario businesses with trusted accounting professionals.
              </p>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <button onClick={() => scrollToSection('about')} className="text-slate-300 hover:text-white transition-colors text-sm">About Us</button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('services')} className="text-slate-300 hover:text-white transition-colors text-sm">Services</button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('industries')} className="text-slate-300 hover:text-white transition-colors text-sm">Industries</button>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Contact</h3>
              <div className="space-y-3">
                <button onClick={() => scrollToSection('contact')} className="flex items-center space-x-2 text-slate-300 hover:text-white transition-colors text-sm">
                  <Mail className="w-4 h-4" />
                  <span>info@honestaccountants.ca</span>
                </button>
                <div className="flex items-center space-x-2 text-slate-300 text-sm">
                  <MapPin className="w-4 h-4" />
                  <span>Serving Ontario</span>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-700 pt-8 text-center">
            <p className="text-slate-400 text-sm">
              © {new Date().getFullYear()} Honest Accountants. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

    </div>);

};

export default Home;